import { femaleQuestionMedium, maleQuestionMedium, bulbMedium } from '@/assets/icons';
import { WITHOUT_DATA, WITHOUT_DATA_SOURCE } from './quotes';

const INDICATORS = `
<p>
  Performance management through continuous improvement must be customer-driven. The customer's needs must be the top priority for the company's value creation.
  This customer can be the entity to which a product is sold, at the end of a transaction, but it can also be an in-house customer, i.e. another team or entity within the same company.<br>
  Management of performance relies on strong indicators (also called KPI) which will allow the team to monitor performances and take actions when the objectives are not met.
</p>
<h2 class="basic-sub-title">
  What is an indicator ?
</h2>
<div class="ia-sensei-quote">
  <span class="quote">"${WITHOUT_DATA}"</span>
  <br>
  <span class="author">${WITHOUT_DATA_SOURCE}</span>
</div>
<p>
  Nowadays, too many decisions are made on the basis of judgement; good performance management eradicates assumptions and replaces them with tangible data.
  Measurement therefore enables gathering data and gaining an objective perspective on a process.
</p>
<ul>
  Measurement may be:
  <li>in real time: via a SPC (Statistical Process Control) system for example, which collects data in real time,</li>
  <li>periodic: every hour, the number of pieces produced is counted,</li>
  <li>by sampling or survey: to measure customer satisfaction, for example.</li>
</ul>
<p>
  Regardless of the type of measurement or its frequency, it will be connected to a precise indicator.
  In order to define a good indicator, it must be related in some way to customer satisfaction.<br>
  Two types of indicators can be used: process indicators and result indicators.
  They should tell us whether the result is compliant and whether the process has been properly followed.<br>
  We generally have results indicators in companies, but process indicators are often missing, although it is often the non-compliance of a process that leads to a lack of quality or a missed delivery time, for example.
</p>
<div class="ia-sensei-example">
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="${bulbMedium}"></path></svg>
  <div class="example-title">As an example :</div>
  We are going to follow a recipe for a cake. The recipe will naturally give us the right
  process indicators that we can follow, while the result of the cake making is the cake itself.
  As process indicators, we may want to choose the oven temperature, the baking time, the
  order of the ingredients, ... and the result indicators could be
  the size and texture of the cake, a survey of guests, etc.
</div>
<div class="ia-sensei-question">
  <div class="question-decoration"></div>
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="${femaleQuestionMedium}"></path></svg>
  <span class="question">Indicators, check. Let's get to it !</span>
</div>
<p>
  Hold on a second.<br>
  It's one thing to know what type of data is collected. But you need to be able to make decisions
  and to take actions quickly when something is wrong. Simply lookin at the data won't tell you if
  there's a problem, unless you are a data analysis expert.<br>
  That's where the objective comes in.
</p>
<h2 class="basic-sub-title">
  SMART Objectives
</h2>
<p>
  The correct definition of an objective is essential when it comes to indicators
  because indicators are often monitored on a daily basis.<br>
  <strong>SMART</strong> is an acronym for how to set and communicate about an objective.<br>
</p>
<ul>
  <li>
    <strong>S</strong> for Specific : The objective must be concrete, quantified. Remaining
    vague does not allow one to have a picture of the results. For example: "Reducing the number of defects"
    is not a specific objective.<br>"Limit the number of defects to 12 per day": This objective
    allows everyone to know that the day's performance was good or conversely that improvements can be made.
  </li>
  <div class="ia-sensei-example">
    Temperature of the oven during baking.
  </div>
  <li>
    <strong>M</strong> for Measurable: We are in the middle of it, it's all about measurement.
    Everyone must be able to measure the deviation from the indicator accurately.
  </li>
  <div class="ia-sensei-example">
    A thermometer should do the job.
  </div>
  <li>
    <strong>A</strong> for Achievable: The objective must be achievable or doable.
    Ambition must be set aside when setting a goal, an ambitious goal is the enemy of daily
    performance management. Indeed, the team may witness values that are too far from the objectives.
  </li>
  <div class="ia-sensei-example">
    Seems like a piece of cake, hey?
  </div>
  <li>
    <strong>R</strong> for Realistic: The objective must be in agreement with the available resources
    and therefore realistic.
  </li>
  <div class="ia-sensei-example">
    Let's check the oven settings... check!
  </div>
  <li>
    <strong>T</strong> for Time: The objective must be reached at a specific time.
    Let's consider \"Reducing the number of defects\". It is impossible to know when
    the objective has been reached. In daily performance management, a value is set
    to be achieved (or NOT achieved) during the day.
  </li>
  <div class="ia-sensei-example">
    Duration from the recipe: 20 minutes.
  </div>
</ul>
<div class="ia-sensei-question">
  <div class="question-decoration"></div>
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="${femaleQuestionMedium}"></path></svg>
  <span class="question">So let's say an indicator exceeds the objective. Should I report a problem?<span>
</div>
<p>
  Precisely. An objective being exceeded cold mean two things: Either the objective hasn't been properly
  defined which can happen when setting up new indicators, or there's a problem.<br>
  Report a problem in order to investigate and search its root causes thanks
  to the tools that Shizen provides.
</p>
`;

const INDICATORS_TITLE = 'Indicators and objectives';

export default {
  INDICATORS,
  INDICATORS_TITLE,
};
